import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

const baseCardStyle = {
    border: 'none',
    boxShadow: '0 4px 10px rgba(233, 30, 99, 0.12)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    margin:'0'
};

const summaryWrapperStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    width: '100%',
    padding: '0.75rem 0.5rem',
};

const thumbnailStyle = {
    width: '64px',
    height: '64px',
    borderRadius: '12px',
    backgroundColor: 'rgba(233, 30, 99, 0.08)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
};

const thumbnailImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
};

const thumbnailIconStyle = {
    position: 'absolute',
    bottom: '6px',
    right: '6px',
    backgroundColor: 'rgba(233, 30, 99, 0.85)',
    color: '#fff',
    borderRadius: '999px',
    padding: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    boxShadow: '0 4px 12px rgba(233, 30, 99, 0.35)',
};

const summaryContentStyle = {
    flex: 1,
    minWidth: 0,
};

const summaryTitleStyle = {
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '0.15rem',
    lineHeight: 1.3,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};

const summaryMetaStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.25rem',
    flexWrap: 'wrap',
};

const typeChipStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.25rem 0.65rem',
    borderRadius: '999px',
    backgroundColor: 'rgba(233, 30, 99, 0.12)',
    color: '#e91e63',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const urlStyle = {
    fontSize: '0.8rem',
    color: '#6c757d',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
};

const summaryIconStyle = {
    fontSize: '28px',
    color: '#e91e63',
};

const cardBodyStyle = {
    padding: '0.75rem 1rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
};

const StreamSummaryCard = ({
    positionLabel,
    title,
    typeLabel,
    typeIcon,
    sourceDetail = '',
    urlDisplay,
    urlTooltip,
    thumbnailUrl = null,
    onEdit,
    onRemove,
    onCardClick = null,
    removeLabel,
    editLabel,
    cardRef = null,
    draggable = false,
    onDragStart,
    onDragEnd,
    onThumbnailError,
}) => {
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setImageFailed(false);
    }, [thumbnailUrl]);

    const showThumbnail = Boolean(thumbnailUrl) && !imageFailed;

    return (
        <div
            className="card dw-stream-card"
            ref={cardRef}
            draggable={draggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            style={baseCardStyle}
        >
            <div className="d-flex justify-content-between align-items-center px-3 pt-3">
                <small className="text-muted text-uppercase" style={{ letterSpacing: '0.05em' }}>{positionLabel}</small>
                <div className="btn-group" role="group" aria-label="stream actions">
                    <button type="button" className="btn btn-link btn-sm text-rose" onClick={onEdit} title={editLabel}>
                        <i className="material-icons">edit</i>
                    </button>
                    <button type="button" className="btn btn-link btn-sm text-danger" onClick={onRemove} title={removeLabel}>
                        <i className="material-icons">close</i>
                    </button>
                </div>
            </div>
            <div style={cardBodyStyle}>
                <div style={{ ...summaryWrapperStyle, cursor: onCardClick ? 'pointer' : 'default' }} onClick={onCardClick}>
                    <div style={thumbnailStyle}>
                        {showThumbnail ? (
                            <>
                                <img
                                    src={thumbnailUrl}
                                    alt=""
                                    style={thumbnailImageStyle}
                                    onError={() => {
                                        setImageFailed(true);
                                        if (onThumbnailError && thumbnailUrl) {
                                            onThumbnailError(thumbnailUrl);
                                        }
                                    }}
                                />
                                <span className="material-icons" style={thumbnailIconStyle}>{typeIcon}</span>
                            </>
                        ) : (
                            <i className="material-icons" style={summaryIconStyle}>{typeIcon}</i>
                        )}
                    </div>
                    <div style={summaryContentStyle}>
                        <div style={summaryTitleStyle} title={title}>{title}</div>
                        <div style={summaryMetaStyle}>
                            <span style={typeChipStyle}>
                                <i className="material-icons" style={{ fontSize: '16px' }}>{typeIcon}</i>
                                <span>{typeLabel}</span>
                            </span>
                            {sourceDetail ? (
                                <span
                                    className="text-muted small"
                                    style={{ maxWidth: '240px', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                                    title={sourceDetail}
                                >
                                    {sourceDetail}
                                </span>
                            ) : null}
                        </div>
                        <div style={urlStyle} title={urlTooltip}>
                            {urlDisplay}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

StreamSummaryCard.propTypes = {
    positionLabel: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    typeLabel: PropTypes.string.isRequired,
    typeIcon: PropTypes.string.isRequired,
    sourceDetail: PropTypes.string,
    urlDisplay: PropTypes.string.isRequired,
    urlTooltip: PropTypes.string.isRequired,
    thumbnailUrl: PropTypes.string,
    onEdit: PropTypes.func.isRequired,
    onRemove: PropTypes.func.isRequired,
    onCardClick: PropTypes.func,
    removeLabel: PropTypes.string.isRequired,
    editLabel: PropTypes.string.isRequired,
    cardRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.any }),
    ]),
    draggable: PropTypes.bool,
    onDragStart: PropTypes.func,
    onDragEnd: PropTypes.func,
    onThumbnailError: PropTypes.func,
};

export default StreamSummaryCard;
